/**
 * Integrytas Group — Common Security Module (CSM)
 * Page: CSM.APPLICATION.LIST  (Base Tables → Platform → Applications)
 *
 * Maintenance for csm_application_master — the applications a page can belong to
 * (the BASE platform itself plus any other/derived applications). A page's
 * Application is chosen on the Pages screen; this screen defines the list.
 *
 * Lookup master + _tl (§8): the master routes through the Common Routine
 * (routedInsert/routedUpdate); the composite-PK _tl companion is written
 * directly alongside its parent (the same §8 exception csm_page_master_tl uses).
 * Personalization-wired per §17.A; Active facet defaults to Active-only (§17.B12);
 * related-children expand surfaces the per-locale translations (§17.B13).
 */
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { apiDb } from '../../lib/api_client';
import { routedInsert, routedUpdate } from '../../lib/routed_writes';
import { useActiveLocale } from '../../lib/use_active_locale';
import type { AuthSession } from '../../types/auth';
import { Modal } from '../../components/common/Modal';
import { EmptyState } from '../../components/common/EmptyState';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { YesNoBadge } from '../../components/common/YesNoBadge';
import { TranslationsEditor } from '../../components/common/TranslationsEditor';
import { buildTranslationsGroup } from '../../components/common/translations_group';
import type { TranslationRow } from '../../components/common/TranslationsEditor';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, PersonalizedField, GRID_TABLE_CLASS } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';
import { hasAccess } from '../../types/auth';

const PAGE_CODE = 'CSM.APPLICATION.LIST';

const APPLICATION_PAGE = 'CSM.APPLICATION.LIST';

const inputCls = 'mb-3 h-8 w-full rounded border border-csm-border px-2 text-csm-sm';
const labelCls = 'mb-1 block text-csm-sm font-medium text-csm-ink-soft';
const btnCls = 'h-7 rounded border border-csm-border bg-white px-2 text-csm-sm hover:bg-csm-surface-alt';
const primaryBtnCls = 'h-7 rounded border border-primary bg-primary px-2 text-csm-sm text-white hover:bg-primary-hover';

interface AppTl { locale_code: string; display_text: string; }

interface AppRow {
  application_id: number;
  internal_code: string;
  is_default: 'Y' | 'N';
  is_active: 'Y' | 'N';
  tl: AppTl[];
}

/**
 * A page belonging to an application (csm_page_master.application_id) — the
 * Base Application's own React pages, or another application's capability
 * catalog rows. Surfaced as the row's second related-children group.
 */
interface AppPage {
  page_id: number;
  application_id: number;
  internal_code: string;
  is_active: 'Y' | 'N';
  tl: AppTl[];
}

const APP_COLS: GridColumnDef[] = [
  { key: 'internal_code', header: 'Code', defaultWidth: 160 },
  { key: 'display_text',  header: 'Name', defaultWidth: 280 },
  { key: 'is_default',    header: 'Default', defaultWidth: 90 },
  { key: 'is_active',     header: 'Active', defaultWidth: 80 },
];

const tlName = (tl: AppTl[], loc: string) =>
  tl.find(x => x.locale_code === loc)?.display_text
  ?? tl.find(x => x.locale_code === 'EN_US')?.display_text
  ?? '';

function CsmApplicationInner({ session }: { session: AuthSession }) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canAdd = hasAccess(session, PAGE_CODE, 'add');
  const canChange = hasAccess(session, PAGE_CODE, 'change');

  const { t } = useTranslation();
  const locale = useActiveLocale();
  const [rows, setRows] = useState<AppRow[]>([]);
  const [pages, setPages] = useState<AppPage[]>([]);
  const [filter, setFilter] = useState('');
  // §17.B12: Active facet defaults to Active-only on first visit.
  const [activeFacet, setActiveFacet] = useState<string[]>(['Y']);
  const [editing, setEditing] = useState<AppRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data, error: e } = await apiDb.from('csm_application_master').select(`
        application_id, internal_code, is_default, is_active,
        csm_application_master_tl ( locale_code, display_text )
      `).order('internal_code');
      if (e) throw e;
      type Raw = Omit<AppRow, 'tl'> & { csm_application_master_tl?: AppTl[] };
      setRows((data ?? []).map((r: Raw) => ({
        application_id: r.application_id,
        internal_code: r.internal_code,
        is_default: r.is_default,
        is_active: r.is_active,
        tl: r.csm_application_master_tl ?? [],
      })));

      // The pages that belong to each application (csm_page_master.application_id).
      // Fetched alongside the masters rather than on expand, because the
      // disclosure control must know up front whether a row has children.
      const { data: pageData, error: pe } = await apiDb.from('csm_page_master').select(`
        page_id, application_id, internal_code, is_active,
        csm_page_master_tl ( locale_code, display_text )
      `).order('internal_code');
      if (pe) throw pe;
      type RawPage = Omit<AppPage, 'tl'> & { csm_page_master_tl?: AppTl[] };
      setPages((pageData ?? []).map((p: RawPage) => ({
        page_id: p.page_id,
        application_id: p.application_id,
        internal_code: p.internal_code,
        is_active: p.is_active,
        tl: p.csm_page_master_tl ?? [],
      })));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { void reload(); }, []);

  // §17.B13: related-children expand/contract (keyed by application_id). Surfaces
  // the row's per-locale translations and the pages that belong to it.
  const detail = useExpandedRows<number>();
  const defaultAppId = rows.find(r => r.is_default === 'Y')?.application_id;
  const buildRelatedGroups = (id: number): RelatedChildGroup[] => {
    const row = rows.find(r => r.application_id === id);
    if (!row) return [];
    // A legacy page predating csm_page_master.application_id carries NULL, which
    // the platform reads as the default application (see the app-scoped catalog
    // migration) — so it must not vanish from the default application's children.
    const ownPages = pages.filter(p =>
      p.application_id === id || (p.application_id == null && id === defaultAppId));
    return [
      buildTranslationsGroup(row.tl, t, locale),
      {
        key: 'pages',
        title: t('application.related.pages', 'Pages'),
        // Drill-down opens the page's OWN editor (registered by PageMasterPage).
        table: 'csm_page_master',
        pkColumn: 'page_id',
        items: ownPages.map(p => ({
          id: p.page_id,
          label: tlName(p.tl, locale) || p.internal_code, /* fk-display-ok: a catalog row registered without a translation still has to be identifiable */
          title: p.internal_code,
          badges: p.is_active === 'N' ? [t('common.boolean.inactive', 'Inactive')] : undefined,
        })),
      },
    ];
  };
  const hasRelated = (id: number) => buildRelatedGroups(id).some(g => g.items.length > 0);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rows.filter(r =>
      (!q || r.internal_code.toLowerCase().includes(q) || tlName(r.tl, locale).toLowerCase().includes(q)) &&
      (activeFacet.length === 0 || activeFacet.includes(r.is_active))
    );
  }, [rows, filter, activeFacet, locale]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-10 shrink-0 items-center gap-2 border-b border-csm-border bg-white px-3">
        <span className="text-csm-sm font-semibold uppercase tracking-wider text-csm-ink-soft">{t('application.title', 'Applications')}</span>
        <input
          type="text"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          placeholder={t('common.filter', 'Filter…')}
          className="h-7 w-44 rounded border border-csm-border px-2 text-csm-sm focus:border-csm-watermark focus:outline-none"
        />
        <FacetFilter
          label={t('common.boolean.active', 'Active')}
          options={[
            { value: 'Y', label: t('common.boolean.active', 'Active') },
            { value: 'N', label: t('common.boolean.inactive', 'Inactive') },
          ]}
          selected={activeFacet}
          onChange={setActiveFacet}
        />
        <ClearFiltersButton
          active={filter.trim() !== '' || activeFacet.length > 0}
          onClear={() => { setFilter(''); setActiveFacet([]); }}
        />
        {canAdd && (<button onClick={() => setCreating(true)} className={primaryBtnCls}>
          + {t('common.new', 'New')}
        </button>)}
        <button onClick={() => void reload()} className={btnCls}>
          {t('common.refresh', 'Refresh')}
        </button>
        {busy && <span className="text-csm-xs text-csm-watermark">{t('common.loading', 'Loading…')}</span>}
        {error && <span className="text-csm-xs text-csm-danger">{error}</span>}
        <div className="ml-auto">
          <PersonalizationToggle />
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        <PersonalizedGrid componentKey="grid" columns={APP_COLS}>
          {(visibleCols, widths) => (
            <table className={GRID_TABLE_CLASS}>
              <thead className="sticky top-0 z-10 bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="w-7 border-b border-csm-border px-1 py-1.5" title={t('common.related_lbl', 'Related records')} />
                  {visibleCols.map(col => (
                    <th
                      key={col.key}
                      className="border-b border-csm-border px-3 py-1.5 text-left"
                      style={widths[col.key] ? { width: widths[col.key] } : undefined}
                    >
                      {col.header}
                    </th>
                  ))}
                  <th className="border-b border-csm-border px-3 py-1.5 text-left" style={{ width: '1%' }}>
                    {t('common.actions', 'Actions')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <Fragment key={r.application_id}>
                  <tr className="hover:bg-csm-surface-alt">
                    <td className="border-b border-csm-border-soft px-1 py-1.5 text-center align-middle" onClick={e => e.stopPropagation()}>
                      <RelatedExpandButton
                        open={detail.isOpen(r.application_id)}
                        hasChildren={hasRelated(r.application_id)}
                        onToggle={() => detail.toggle(r.application_id)}
                        expandLabel={t('common.related_expand', 'Show related records')}
                        collapseLabel={t('common.related_collapse', 'Hide related')}
                        tooltip={t('common.related_lbl', 'Related records')}
                      />
                    </td>
                    {visibleCols.map(col => (
                      <td
                        key={col.key}
                        className="border-b border-csm-border-soft px-3 py-1.5"
                        style={widths[col.key] ? { width: widths[col.key] } : undefined}
                      >
                        {col.key === 'internal_code' && <span className="font-mono text-csm-sm">{r.internal_code}</span>} {/* fk-display-ok: application code is a natural identifier */}
                        {col.key === 'display_text' && tlName(r.tl, locale)}
                        {col.key === 'is_default' && <YesNoBadge value={r.is_default} yesLabel={t('common.yes', 'Yes')} noLabel={t('common.no', 'No')} />}
                        {col.key === 'is_active' && <YesNoBadge value={r.is_active} yesLabel={t('common.boolean.active', 'Active')} noLabel={t('common.boolean.inactive', 'Inactive')} />}
                      </td>
                    ))}
                    <td className="border-b border-csm-border-soft px-3 py-1.5 text-right">
                      {canChange && (<button onClick={() => setEditing(r)} className="rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-border-soft">
                        {t('common.edit', 'Edit')}
                      </button>)}
                    </td>
                  </tr>
                  {detail.isOpen(r.application_id) && (
                    <tr>
                      <td colSpan={visibleCols.length + 2} className="border-b border-csm-border bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}>
                        <RelatedChildrenPanel
                          groups={buildRelatedGroups(r.application_id)}
                          session={session}
                          onSaved={() => void reload()}
                        />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
                {filtered.length === 0 && !busy && (
                  <tr>
                    <td colSpan={visibleCols.length + 2} className="py-12 text-center">
                      <EmptyState title={t('common.no_records', 'No records')} />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </PersonalizedGrid>
      </div>

      {(editing || creating) && (
        <ApplicationModal
          session={session}
          row={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); void reload(); }}
        />
      )}
    </div>
  );
}

function ApplicationModal({
  session, row, onClose, onSaved,
}: {
  session: AuthSession;
  row: AppRow | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const isCreate = row === null;
  const [code, setCode] = useState(row?.internal_code ?? '');
  const [isDefault, setIsDefault] = useState(row?.is_default === 'Y');
  const [isActive, setIsActive] = useState(!row || row.is_active === 'Y');
  const [translations, setTranslations] = useState<TranslationRow[]>(row?.tl ?? []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const upsertTranslations = async (applicationId: number) => {
    // Composite-PK _tl companion — written directly alongside its parent (§8).
    for (const tr of translations) {
      if (!tr.locale_code || !tr.display_text?.trim()) continue;
      const { error: e } = await apiDb
        .from('csm_application_master_tl')
        .upsert({
          application_id: applicationId,
          locale_code: tr.locale_code,
          display_text: tr.display_text,
          description: tr.description || null,
        }, { onConflict: 'application_id,locale_code' });
      if (e) throw new Error(e.message);
    }
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const trimmed = code.trim().toUpperCase();
      if (!trimmed) throw new Error(t('common.err.code_required', 'Code is required.'));
      if (isCreate && !/^[A-Z][A-Z0-9_.]*$/.test(trimmed)) {
        throw new Error(t('application.err.code_format', 'Code must start with a letter — uppercase letters, numbers, dots only.'));
      }
      if (!translations.some(tr => tr.display_text?.trim())) {
        throw new Error(t('common.err.translation_required', 'At least one translation is required.'));
      }

      if (isCreate) {
        const { row_id, error: e } = await routedInsert(session, 'csm_application_master', {
          internal_code: trimmed,
          is_default: isDefault ? 'Y' : 'N',
          is_active: isActive ? 'Y' : 'N',
        });
        if (e) throw new Error(e.message);
        if (row_id == null) throw new Error(t('application.err.no_id', 'Could not resolve the new application id.'));
        await upsertTranslations(row_id);
      } else {
        const { error: e } = await routedUpdate(session, 'csm_application_master', 'application_id', row.application_id, {
          is_default: isDefault ? 'Y' : 'N',
          is_active: isActive ? 'Y' : 'N',
        });
        if (e) throw new Error(e.message);
        await upsertTranslations(row.application_id);
      }
      onSaved();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={isCreate ? t('application.new', 'New Application') : `${t('common.edit', 'Edit')} — ${row?.internal_code}`}
      onClose={onClose}
      width="md"
      headerAction={<PersonalizationToggle />}
      footer={
        <>
          <button onClick={onClose} className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft">
            {t('common.cancel', 'Cancel')}
          </button>
          <button
            onClick={() => void save()}
            disabled={busy}
            className="rounded border border-primary bg-primary px-3 py-1 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60"
          >
            {busy ? t('common.saving', 'Saving…') : t('common.save', 'Save')}
          </button>
        </>
      }
    >
      <PersonalizedField componentKey="modal" fieldKey="internal_code" label={t('common.code', 'Code')}>
        <label className={labelCls}>{t('common.code', 'Code')}</label>
        <input
          type="text"
          value={code}
          onChange={e => setCode(e.target.value.toUpperCase())}
          disabled={!isCreate}
          placeholder={t('application.code_placeholder', 'E.g. CRM')}
          className={inputCls + ' font-mono disabled:bg-csm-surface-alt disabled:text-csm-muted'}
        />
      </PersonalizedField>

      <PersonalizedField componentKey="modal" fieldKey="is_default" label={t('application.field.default', 'Default')}>
        <label className="mb-3 flex items-center gap-2 text-csm-sm text-csm-ink-soft">
          <input
            type="checkbox"
            checked={isDefault}
            onChange={e => setIsDefault(e.target.checked)}
            className="h-4 w-4 accent-gray-700"
          />
          {t('application.field.default_hint', 'Default application for new pages')}
        </label>
      </PersonalizedField>

      <PersonalizedField componentKey="modal" fieldKey="is_active" label={t('common.boolean.active', 'Active')}>
        <label className="mb-3 flex items-center gap-2 text-csm-sm text-csm-ink-soft">
          <input
            type="checkbox"
            checked={isActive}
            onChange={e => setIsActive(e.target.checked)}
            className="h-4 w-4 accent-gray-700"
          />
          {t('common.boolean.active', 'Active')}
        </label>
      </PersonalizedField>

      <TranslationsEditor
        value={translations}
        onChange={setTranslations}
        label={t('common.translations_lbl', 'Translations')}
      />

      {error && <div className="mt-3 rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-1 text-csm-sm text-csm-danger">{error}</div>}
    </Modal>
  );
}

export function CsmApplicationPage({ session }: { session: AuthSession }) {
  return (
    <PersonalizationProvider pageCode={APPLICATION_PAGE} session={session}>
      <CsmApplicationInner session={session} />
    </PersonalizationProvider>
  );
}
